/**
 * Модалки и выдвижные панели блокируют прокрутку страницы через
 * `overflow: hidden` на <html> или <body>. Полоса прокрутки при этом
 * исчезает, страница становится шире на её ширину и содержимое дёргается
 * вбок. Здесь, в одном месте для всех окон, на время блокировки к <body>
 * добавляется отступ справа ровно на ширину пропавшей полосы.
 *
 * Резервировать место через `scrollbar-gutter: stable` нельзя: затемнение
 * окна (fixed, inset: 0) тогда не доходит до правого края — браузер не даёт
 * рисовать поверх зарезервированной полосы.
 */

// Стандартная ширина полосы прокрутки браузера; 0 там, где полосы
// накладываются поверх страницы (телефоны, macOS с исчезающими полосами).
// Запасной вариант: у самой страницы полоса может быть перерисована тоньше.
const measureDefaultScrollbarWidth = () => {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
  document.body.appendChild(probe);
  const width = probe.offsetWidth - probe.clientWidth;
  document.body.removeChild(probe);
  return width;
};

export function installScrollLockCompensation() {
  const html = document.documentElement;
  const { body } = document;
  if (!body || typeof MutationObserver === 'undefined') return;

  const defaultWidth = measureDefaultScrollbarWidth();
  let pageScrollbarWidth = 0;
  let applied = false;

  const isLocked = () => html.style.overflow === 'hidden'
    || html.style.overflowY === 'hidden'
    || body.style.overflow === 'hidden'
    || body.style.overflowY === 'hidden';

  // Реальную полосу страницы меряем, пока прокрутка не заблокирована: после
  // блокировки её уже нет. Полоса появляется, когда содержимое дорастает до
  // высоты окна, поэтому следим и за размером body.
  const measurePage = () => {
    if (isLocked()) return;
    const width = window.innerWidth - html.clientWidth;
    if (width > 0) pageScrollbarWidth = width;
  };

  const sync = () => {
    const locked = isLocked();
    if (locked && !applied) {
      // Полоса была, только если содержимое выше окна.
      const hadScrollbar = html.scrollHeight > window.innerHeight;
      const width = pageScrollbarWidth || defaultWidth;
      if (hadScrollbar && width > 0) {
        body.style.paddingRight = `${width}px`;
        applied = true;
      }
    } else if (!locked && applied) {
      body.style.paddingRight = '';
      applied = false;
    } else if (!locked) {
      measurePage();
    }
  };

  const observer = new MutationObserver(sync);
  observer.observe(html, { attributes: true, attributeFilter: ['style'] });
  observer.observe(body, { attributes: true, attributeFilter: ['style'] });

  // Масштаб страницы меняет ширину полосы в CSS-пикселях.
  window.addEventListener('resize', measurePage);
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(measurePage).observe(body);

  measurePage();
  sync();
}
